import type { ReactNode } from 'react';
import { CheckCircle2, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MarketingFooter, MarketingHeader } from '@/modules/marketing/components/site-chrome';
import { usePageSeo } from '@/modules/marketing/hooks/use-page-seo';

export interface MarketingDetailContent {
    eyebrow: string;
    icon: LucideIcon;
    gradient: string;
    title: string;
    tagline: string;
    description: string;
    bullets: string[];
    keywords?: string;
}

function truncate(text: string, max: number) {
    if (text.length <= max) return text;
    return text.slice(0, text.lastIndexOf(' ', max)) + '…';
}

/**
 * Shared page shell for every Industry / Product / Platform-feature detail page — one hero (gradient banner, icon,
 * eyebrow, title, tagline) plus a two-column body (description + bullets + Book a Demo CTA, and a big visual) so all
 * ~17 pages this powers stay visually consistent while their actual content is fully data-driven per page.
 */
export function MarketingDetailPage({
    content,
    backLabel,
    backHref,
    visual,
}: {
    content: MarketingDetailContent;
    backLabel: string;
    backHref: string;
    visual: ReactNode;
}) {
    usePageSeo({
        title: truncate(`${content.title} — ${content.tagline}`, 60),
        description: truncate(content.description, 158),
        keywords: content.keywords || `${content.title}, ${content.eyebrow}, AI assistant, pricing intelligence, AIPA`,
    });

    return (
        <div className="min-h-screen bg-background text-foreground">
            <MarketingHeader />

            <section className={`bg-gradient-to-br ${content.gradient} py-20 text-white`}>
                <div className="mx-auto max-w-4xl px-6 text-center">
                    <Link to={backHref} className="text-sm text-white/70 hover:text-white">
                        ← {backLabel}
                    </Link>
                    <div className="mx-auto mt-5 flex size-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur">
                        <content.icon className="size-7" />
                    </div>
                    <p className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-white/70">{content.eyebrow}</p>
                    <h1 className="mt-2 text-4xl font-extrabold tracking-tight sm:text-5xl">{content.title}</h1>
                    <p className="mx-auto mt-4 max-w-2xl text-lg text-white/80">{content.tagline}</p>
                </div>
            </section>

            <section className="py-16">
                <div className="mx-auto grid max-w-5xl grid-cols-1 gap-12 px-6 lg:grid-cols-2 lg:items-center">
                    <div>
                        <p className="text-muted-foreground">{content.description}</p>
                        <ul className="mt-6 space-y-3">
                            {content.bullets.map((bullet) => (
                                <li key={bullet} className="flex items-start gap-3 text-sm font-medium">
                                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-500" />
                                    {bullet}
                                </li>
                            ))}
                        </ul>
                        <Button size="lg" className="mt-8" asChild>
                            <Link to="/company/book-a-demo">Book a Demo</Link>
                        </Button>
                    </div>

                    <div className="flex justify-center">{visual}</div>
                </div>
            </section>

            <MarketingFooter />
        </div>
    );
}
